export type Language = "it" | "en";

export const translations = {
  it: {
    // Header
    nav: {
      home: "Home",
      products: "Prodotti",
      technology: "Tecnologia",
      benefits: "Benefici",
      configurator: "Configuratore",
      rental: "Noleggi",
      contact: "Contatti",
      rentForEvents: "Noleggia",
      requestQuote: "Acquista",
      askInfo: "Chiedi Info",
      openMenu: "Apri menu",
      closeMenu: "Chiudi menu",
    },

    // Hero
    hero: {
      badge: "L'AI che ha un impatto concreto sul pianeta",
      headline: "Trasforma i",
      headlineHighlight1: "rifiuti",
      headlineIn: "in",
      headlineHighlight2: "dati.",
      subheadline: "La nostra IA proprietaria automatizza lo smistamento con una",
      subheadlineStrong1: "purezza del 95%",
      subheadlineReduces: ", trasformando ogni scarto in",
      subheadlineStrong2: "KPI audit‑ready",
      subheadlineGuarantees: "per rendere i tuoi",
      subheadlineStrong3: "obiettivi ESG misurabili",
      subheadlineImmediate: "in tempo reale.",
      ctaPrimary: "Acquista",
      ctaSecondary: "Noleggia",
    },

    // Partners
    partners: {
      title: "Clienti che con noi migliorano brand e impatto",
      subtitle:
        "Perché oggi ignorare l'impatto ambientale non è solo un errore morale, ma è anche un pessimo affare di business.",
    },

    // Products
    products: {
      title: "La nostra suite di soluzioni",
      titleHighlight: "Hoooly",
      subtitle: "AI che diventa Hardware per ogni esigenza.",
      indoor: {
        name: "HooolyIndoor",
        tagline: "Smistatore automatico ad alto volume",
        idealFor: "Food court, Mall, Aeroporti",
        specs: ["Volume: 240 L", "Display touch", "AI < 1 s", "Plug & Play", "Monitoraggio real-time"],
      },
      small: {
        name: "HooolySmall",
        tagline: "Cestino multiscomparto guidato",
        idealFor: "Uffici, Retail",
        specs: ["Volume: 180 L", "Monitor touch", "AI < 1 s", "Plug & Play", "Monitoraggio real-time"],
      },
      food: {
        name: "HooolyFood",
        tagline: "Sensore di spreco alimentare",
        idealFor: "Mense, Ristoranti, Hotel",
        specs: ["Riconoscimento piatti", "Peso automatico", "Strategie consigliate", "Ottimizzazione dei costi"],
      },
      dashboard: {
        name: "HooolyDashboard",
        tagline: "Intelligence Platform",
        idealFor: "Management, Sustainability",
        specs: ["Agent AI", "Report ESG", "Export dati & KPI"],
      },
      techTitle: "Le performance che",
      techTitleHighlight: "Hoooly",
      techTitleEnd: "garantisce",
      techSubtitle: "Non solo ideali, ma solida tecnologia.",
      techFeatures: {
        aiRecognition: {
          title: "AI Recognition",
          stat: "95%",
          statLabel: "accuratezza certificata",
          description:
            "Riconoscimento in tempo reale in meno di 1s di materiali e brand con 95% di accuratezza certificata.",
        },
        sorting: {
          title: "Smistamento potenziato",
          stat: "-40%",
          statLabel: "volume indifferenziato",
          description: "Presenza di frazioni estranee del 5% nelle varie categorie.",
        },
        continuity: {
          title: "Continuità operativa",
          stat: "24/7",
          statLabel: "servizio garantito",
          description: "Servizio garantito 24/7, anche in caso di guasto.",
        },
        design: {
          title: "Design plug & play",
          stat: "5min",
          statLabel: "installazione",
          description: "Installazione in 5 minuti, Wi‑Fi e 4G integrati.",
        },
      },
    },

    // Benefits (Bento)
    benefits: {
      title: "Sostenibilità che diventa",
      titleHighlight: "ROI",
      subtitle: "L'Europa impone nuove regole. Chi non si adegua paga di più, perde clienti e accesso al credito.",
      features: {
        efficiency: {
          title: "Efficienza operativa",
          stat: "-40%",
          statLabel: "costi di gestione operativi",
          description: "Monitoraggio intelligente, svuotamenti ottimizzati e riduzione dei costi di smaltimento rifiuti grazie alla Computer Vision AI.",
        },
        compliance: {
          title: "Costo del capitale",
          stat: "+2%",
          statLabel: "interessi bancari per rating ESG basso",
          description: "Le banche valutano il rischio climatico. Chi non traccia i rifiuti è \"ad alto rischio\". Hoooly migliora il rating Environmental, abbassando il costo dei prestiti e aprendo l'accesso ai Green Loans.",
        },
        fiscal: {
          title: "Tasse e sanzioni",
          stat: "TARI",
          statLabel: "riduzione quota variabile",
          description:
            "Plastic Tax, aumento tariffe di smaltimento: gestire male i rifiuti costa migliaia di euro l'anno. Hoooly riduce l'indifferenziato e abbatte la base imponibile, con dati certificati a supporto.",
        },
        sentiment: {
          title: "Rischio greenwashing",
          stat: "CSRD",
          statLabel: "obbligo di reporting certificato",
          description: "La CSRD impone dati auditabili sull'impatto ambientale. Senza tracciabilità reale, si rischiano sanzioni legali e danni d'immagine. Hoooly certifica ogni grammo, materiale per materiale.",
        },
      },
      cta: "Vuoi vedere i risultati nel tuo contesto?",
      ctaLink: "Richiedi una demo →",
    },

    // Configurator
    configurator: {
      title: "Simula l’impatto di Hoooly",
      titleHighlight: "nei tuoi spazi",
      subtitle: "In meno di 1 minuto scopri risparmi, efficienza e impatto ambientale reale.",
      subtitleInfo: "Ogni singolo kg di rifiuto che le nostre soluzioni sottraggono all'indifferenziata per avviarlo al recupero risparmia all'ambiente 20 litri d'acqua e 1,5 kg di CO₂.",
      requestConsultation: "Richiedi una consulenza gratuita",
      step1: "Seleziona il tuo settore",
      step2: "Inserisci i tuoi dati",
      calculate: "Scopri la tua soluzione",
      results: "I tuoi risultati",
      resultsSubtitle: "Ecco l'impatto possibile",
      newCalculation: "Nuovo calcolo",
      goToConfigurator: "Vai al Configuratore",
      sectors: {
        offices: {
          label: "Media affluenza",
          description: "Uffici, Retail",
        },
        retail: {
          label: "Grande affluenza",
          description: "Mall, Aeroporti",
        },
        canteens: {
          label: "Mense",
          description: "HoReCa, Ospedali, Corporate",
        },
      },
      inputs: {
        dailyFootfall: "Persone che utilizzano lo spazio ogni giorno",
        dailyMeals: "Pasti giornalieri",
        placeholderFootfall: "es. 50",
        placeholderRetail: "es. 1000",
        placeholderMeals: "es. 200",
        surface: "Superficie (mq)",
        placeholderSurface: "es. 500",
        emptyingsPerDay: "Svuotamenti giornalieri",
        emptyingsQuestion: "Quante volte al giorno viene effettuata la pulizia/svuotamento?",
        percentIndiff: "% Indifferenziata",
        percentIndiffDesc: "Percentuale di rifiuti indifferenziati rispetto al totale",
        percentIndiffPreset: "Valore preimpostato sulla media del settore, modificabile",
        kgWasteDay: "Kg rifiuti/giorno",
        placeholderKgWaste: "es. 10",
        kgWasteDesc: "Rifiuto totale prodotto giornalmente",
        kgWasteAutoCalc: "Calcolato automaticamente in base all'affluenza, modificabile",
      },
      results_labels: {
        wasteReduction: "Riduzione Indifferenziata",
        foodWasteReduction: "Riduzione Spreco",
        wasteDescDynamic: "Da {from}% a 10% di indifferenziata",
        wasteDescAlreadyOptimal: "Il tuo impatto è già grandioso! Mantieni il 10% di indifferenziata",
        foodWasteDesc: "50% riduzione spreco alimentare",
        co2Saved: "CO₂ Risparmiata",
        co2Desc: "Coefficiente: {coeff} kg CO₂ per kg rifiuto",
        waterSaved: "Acqua Risparmiata",
        waterDesc: "Coefficiente: {coeff} L per kg rifiuto",
        logistics: "Ottimizzazione Logistica",
        logisticsDesc: "Svuotamenti risparmiati grazie alla corretta differenziazione",
        emptyingsPerDay: "svuotamenti/giorno risparmiati",
        purchaseAdvice: "Consiglio d'Acquisto",
        purchaseDesc: "Numero consigliato di unità {product}",
        units: "unità",
        product: "Prodotto",
        calculatedBased: "Calcolato in base a volume rifiuti ({rt} kg/giorno) e superficie ({area} mq)",
        methodology:
          "I parametri di risparmio sono calcolati sulla base di medie nazionali LCA (Life Cycle Assessment) e report FAO/ISPRA, riferiti all'impatto ambientale evitato nei processi di produzione e smaltimento delle materie prime.",
        methodologyTooltip: "Parametri basati su Life Cycle Assessment e report FAO/ISPRA",
      },
    },

    // Services
    services: {
      title: "Modelli di",
      titleHighlight: "attivazione",
      purchase: {
        title: "Acquisto",
        subtitle: "Per Uffici, Mall, Aeroporti, Retail",
        description: "Investimento CAPEX proprietario.",
        features: ["Proprietà del dispositivo", "Dashboard SaaS inclusa", "Supporto prioritario 24/7"],
        cta: "Richiedi Preventivo",
      },
      longRental: {
        title: "Noleggio lungo",
        subtitle: "Contratto 12-36 mesi",
        description: "Modello OpEx con canone mensile e manutenzione inclusa.",
        features: ["Nessun investimento iniziale", "Manutenzione inclusa", "Upgrade tecnologico garantito"],
        cta: "Scopri i Piani",
      },
      shortRental: {
        title: "Noleggio breve",
        subtitle: "Per Festival, Fiere e Eventi",
        description: "Noleggio flessibile con setup e ritiro inclusi.",
        features: ["Setup e ritiro inclusi", "Report post-evento", "Assistenza in loco"],
        cta: "Noleggia Ora",
      },
    },

    // Awards
    awards: {
      badge: "Riconoscimenti",
      title: "Premi e Riconoscimenti",
      subtitle: "La nostra innovazione è stata riconosciuta a livello internazionale",
      items: {
        lifeTriplAir: {
          title: "European Life Tripl-Air Tender",
          subtitle: "Selezionati per il bando europeo",
        },
        googleAI: {
          title: "Google AI Sustainability Award",
          subtitle: "Vincitori del premio AI Groundbreaker",
        },
        luiss: {
          title: "Non Sprecare Award",
          subtitle: "Vincitori del premio LUISS University - Roma",
        },
        techcrunch: {
          title: "TechCrunch Disrupt 2025",
          subtitle: "Selezionati per San Francisco",
        },
      },
    },

    // Contact
    contact: {
      title: "Parliamo del tuo",
      titleHighlight: "progetto",
      subtitle: "Compila il form e ti ricontatteremo entro 24 ore con una proposta personalizzata.",
      location: "Sede",
      email: "Email",
    },

    // Footer
    footer: {
      description: "La prima piattaforma AI che trasforma i rifiuti in dati per aziende sostenibili.",
      products: "Prodotti",
      company: "Azienda",
      solutions: "Soluzioni",
      solutionLinks: {
        tariReduction: "Riduzione TARI",
        esgReporting: "Compliance CSRD & ESG",
        eventRental: "Noleggio per Eventi",
        operationalCosts: "Plastic Tax & Green Loans",
      },
      aboutUs: "Chi siamo",
      caseStudy: "Case Study",
      careers: "Lavora con noi",
      contact: "Contatti",
      legal: "Legal",
      privacyPolicy: "Privacy Policy",
      cookiePolicy: "Cookie Policy",
      companyName: "Ragione Sociale",
      headquarters: "Sede Legale",
      vatNumber: "P.IVA / C.F.",
      copyright: "© {year} Ganiga Innovation Srl. Tutti i diritti riservati.",
      madeWith: "Made with 💚 in Italy",
    },

    // Careers
    careers: {
      title: "Lavora",
      titleHighlight: "con noi",
      subtitle: "Cerchiamo talento, vision, velocità.",
      firstName: "Nome",
      lastName: "Cognome",
      email: "Email",
      role: "Ruolo di interesse",
      rolePlaceholder: "Seleziona un ruolo",
      cv: "Allega CV",
      cvUpload: "Clicca per caricare il tuo CV",
      cvFormat: "PDF o Word, max 10MB",
      message: "Altro da aggiungere?",
      messageOptional: "(opzionale)",
      messagePlaceholder: "Raccontaci qualcosa di te, le tue motivazioni o cosa ti ha colpito di Ganiga...",
      submit: "Invia Candidatura",
      submitting: "Invio in corso...",
      privacy: "I tuoi dati saranno trattati secondo la nostra Privacy Policy.",
      errors: {
        fileTooLarge: "Il file è troppo grande. Massimo 10MB.",
        invalidFormat: "Formato non supportato. Usa PDF o Word.",
        fillRequired: "Compila tutti i campi obbligatori.",
        attachCV: "Allega il tuo CV.",
      },
      success: "Candidatura inviata con successo! Ti contatteremo presto.",
      roles: {
        other: "Altro",
      },
    },

    // Rental Page
    rental: {
      badge: "Noleggio per Eventi",
      heroTitle: "Gestione Integrale Rifiuti per Eventi",
      heroTitleHighlight: "ISO 20121",
      heroSubtitle:
        "Soluzione completa di facility management: tecnologia AI Hoooly, personale operativo dedicato e reportistica certificata per la massima conformità ESG.",
      heroSubtitleBold: ["facility management", "tecnologia AI Hoooly", "personale operativo dedicato", "reportistica certificata", "conformità ESG"],
      heroIsoBadge: "ISO 20121 Ready",
      ctaPrimary: "Richiedi un piano di Facility Management",
      ctaSecondary: "Scopri i Vantaggi",

      // Compliance Highlight Box (post-hero)
      complianceBox: {
        label: "Standard ISO 20121",
        text: "I nostri report digitali e la precisione del sorting AI (>95%) forniscono l'evidenza tecnica necessaria per la certificazione del tuo evento e la rendicontazione CSRD.",
      },

      // Facility Management Pillars
      pillars: {
        title: "Smart Waste Operations: il servizio in 3 pillar",
        subtitle: "Un unico interlocutore per hardware, presidio operativo e dati certificati.",
        items: {
          hardware: {
            label: "Hardware",
            title: "Smart Bin Hoooly",
            description: "Noleggio della flotta Hoooly Indoor e Small con AI di riconoscimento e connessione 4G nativa.",
          },
          staff: {
            label: "Staff",
            title: "Presidio Operativo",
            description: "Personale Ganiga on-site per gestione flussi, svuotamento cestini e supporto continuo.",
          },
          data: {
            label: "Data",
            title: "Dashboard & Report",
            description: "Monitoraggio in tempo reale e report post-evento certificato, pronto per audit ESG e ISO 20121.",
          },
        },
      },

      // Operational Excellence Section
      operationalTitle: "Eccellenza Operativa",
      operationalSubtitle: "Tre pilastri per una gestione senza pensieri",
      operational: {
        plugPlay: {
          title: "Plug & Play",
          description: "Installazione in",
          descriptionEnd: ", connessione 4G nativa.",
        },
        aiSorting: {
          title: "AI Sorting",
          descriptionPre: "Precisione al",
          descriptionMid: ", riduce l'indifferenziata al",
          descriptionEnd: ".",
        },
      },

      // Data Impact Section
      dataImpact: {
        title: "I Numeri che Contano",
        subtitle: "KPI misurabili che dimostrano l'efficienza matematica, non solo teorica",
        precision: {
          title: "Precisione AI",
          description: "Riconoscimento automatico dei materiali",
        },
        waste: {
          title: "Indifferenziata",
          description: "Residuo abbattuto al minimo",
        },
        costs: {
          title: "Costi Logistici",
          description: "Svuotamenti e personale ottimizzati",
        },
      },

      // ISO 20121 Section
      iso: {
        badge: "Vantaggio ISO 20121",
        title: "Compliance ISO 20121 & CSRD",
        subtitle: "Tre evidenze tecniche che rendono il tuo evento certificabile.",
        columns: [
          {
            title: "Audit-Ready Data",
            description: "Report digitali con tracciabilità totale dei flussi (kg, CO₂, purezza) per rendicontazione ISO 20121 e CSRD.",
          },
          {
            title: "95% Sorting Accuracy",
            description: "Precisione AI garantita: superiamo i requisiti minimi di legge sugli obiettivi di riciclo.",
          },
          {
            title: "ESG Reputation",
            description: "Valore tangibile per sponsor e stakeholder: migliora il rating di sostenibilità del tuo evento.",
          },
        ],
      },

      comparison: {
        title: "Perché Ganiga conviene?",
        without: {
          title: "Senza Ganiga",
          items: ["Differenziata sporca", "Costi di smaltimento elevati", "Impatto ambientale incerto"],
        },
        with: {
          title: "Con Ganiga",
          items: [
            "95% di precisione nel riconoscimento",
            "Solo il 10% di indifferenziata",
            "Report ESG pronto all'uso",
          ],
        },
      },

      // Product Fleet Section
      fleetTitle: "La Tua Flotta Intelligente",
      fleetSubtitle: "Due soluzioni complementari per coprire ogni esigenza del tuo evento",
      indoor: {
        title: "Hoooly Indoor",
        subtitle: "Smistatore automatico ad alto volume",
        badge: "INDOOR",
        description: "Connessione 4G integrata: non serve il Wi-Fi della location, accendi e sei pronto.",
        idealFor: "Ideale per: Aree Food, Zone Lounge, Ingressi principali",
        specs: {
          volume: "Volume",
          volumeValue: "240 L",
          display: "Display",
          displayValue: "Touch",
          ai: "AI Recognition",
          aiValue: "< 1s",
          setup: "Connessione",
          setupValue: "4G integrato",
        },
      },
      small: {
        title: "Hoooly Small",
        subtitle: "Cestino multiscomparto guidato",
        badge: "OUTDOOR",
        description: "Connessione 4G integrata: non serve il Wi-Fi della location, accendi e sei pronto.",
        idealFor: "Ideale per: Aree Esterne, Corridoi, Zone Secondarie",
        specs: {
          volume: "Volume",
          volumeValue: "180 L",
          display: "Monitor",
          displayValue: "Touch",
          ai: "AI Recognition",
          aiValue: "< 1s",
          setup: "Connessione",
          setupValue: "4G integrato",
        },
      },

      // Revenue & Branding Section
      revenue: {
        title: "Il Noleggio che Si Ripaga",
        description:
          "Trasforma un servizio igienico in un asset di marketing. I nostri modelli Hoooly sono magneti per l'attenzione: personalizzali con il brand dei tuoi sponsor e crea nuovi spazi pubblicitari ad alta visibilità.",
        descriptionBold: ["asset di marketing", "magneti per l'attenzione", "spazi pubblicitari ad alta visibilità"],
        benefits: [
          "Superficie brandizzabile per sponsor",
          "Display touch per comunicazione dinamica",
          "Report finale con engagement metrics",
        ],
      },
      economicBenefit: {
        title: "Benefit Economico",
        description:
          "Meno indifferenziata significa costi di smaltimento rifiuti drasticamente ridotti per l'ente organizzatore. Ganiga non è solo una scelta green, è un'ottimizzazione di budget.",
        descriptionBold: ["costi di smaltimento rifiuti drasticamente ridotti", "ottimizzazione di budget"],
      },

      // ESG Reporting Section
      esg: {
        title: "Soluzione All-Inclusive di Facility Management",
        subtitle:
          "Tecnologia, operatività e dati: un unico pacchetto per il facility management sostenibile di fiere, festival e grandi eventi.",
      },
      includes: {
        setup: {
          title: "Zero Pensieri",
          description: "Logistica completa, posizionamento strategico dei cestini e ritiro a fine evento.",
        },
        support: {
          title: "Staff Dedicato",
          description: "Personale specializzato Ganiga on-site per monitoraggio flussi e svuotamento cestini.",
        },
        report: {
          title: "Dati & Report",
          description: "Dashboard live e report finale con KPI: CO₂, kg per materiale, purezza differenziata.",
        },
        marketing: {
          title: "Branding Sponsor",
          description: "Personalizza i cestini con il brand dei tuoi sponsor per nuove visibilità pubblicitarie.",
        },
      },

      // FAQ Section (AI/SEO indexing)
      faq: {
        title: "Domande Frequenti",
        subtitle: "Tutto quello che devi sapere sul facility management sostenibile per eventi",
        items: [
          {
            q: "In che modo Ganiga aiuta ad ottenere la ISO 20121?",
            a: "Ganiga fornisce dati certificati e tracciabili sulla gestione dei rifiuti del tuo evento, requisito chiave della norma ISO 20121. Le stazioni Hoooly registrano in tempo reale flussi, peso per materiale e purezza della differenziata, generando report audit-ready che eliminano stime manuali.",
          },
          {
            q: "Il personale Ganiga si occupa anche dello svuotamento dei cestini?",
            a: "Sì. Il nostro pacchetto di facility management include staff dedicato on-site per tutta la durata dell'evento: monitoraggio dei flussi, svuotamento dei cestini, gestione operativa e supporto tecnico. Tu pensi all'evento, noi al ciclo completo dei rifiuti.",
          },
          {
            q: "Come vengono calcolati i dati sui rifiuti dell'evento?",
            a: "Ogni stazione Hoooly utilizza computer vision AI per riconoscere e classificare i materiali con precisione del 95%. I dati (kg per categoria, CO₂ evitata, purezza, riempimento) sono raccolti in tempo reale via 4G e aggregati in una dashboard live, con report finale dettagliato consegnato a fine evento.",
          },
          {
            q: "Per quali tipi di evento è adatto il servizio?",
            a: "Fiere, festival, expo, congressi, eventi sportivi e culturali. Il servizio scala da poche centinaia a decine di migliaia di visitatori al giorno, indoor e outdoor, grazie alla flotta Hoooly Indoor e Small e alla connessione 4G nativa che non richiede infrastrutture sul posto.",
          },
        ],
      },

      // Contact Form
      contactTitle: "Progetta il tuo Evento Sostenibile dalla A alla Z",
      contactSubtitle: "Compila il form e riceverai un piano di facility management personalizzato entro 24 ore.",
      form: {
        firstName: "Nome",
        firstNamePlaceholder: "Il tuo nome",
        lastName: "Cognome",
        lastNamePlaceholder: "Il tuo cognome",
        email: "Email",
        emailPlaceholder: "esempio@email.com",
        phone: "Telefono",
        phonePlaceholder: "Opzionale",
        phoneOptional: "(non obbligatorio)",
        eventStartDate: "Data inizio evento",
        eventDuration: "Durata evento (in giorni)",
        eventDurationPlaceholder: "es. 3",
        eventType: "Tipo di evento",
        eventTypePlaceholder: "es. Festival, Fiera, Expo",
        expectedAttendance: "Affluenza prevista",
        expectedAttendancePlaceholder: "es. 5000",
        description: "Descrizione",
        descriptionPlaceholder: "Descrivi brevemente il tuo evento...",
        company: "Azienda/Organizzazione",
        companyPlaceholder: "Nome azienda",
        location: "Luogo dell'evento",
        locationPlaceholder: "es. Milano, Fiera Rho",
        locationType: "Tipologia di location",
        locationTypeOutdoor: "All'aperto",
        locationTypeIndoor: "Al chiuso",
        locationTypeBoth: "Entrambi",
        collectionPoints: "Numero punti di raccolta desiderati",
        collectionPointsPlaceholder: "es. 10",
      },
      configurator: {
        title: "Calcola l'Impatto del Tuo Evento",
        subtitle: "Seleziona un preset o inserisci i tuoi parametri per scoprire il potenziale impatto ambientale",
        step1: "Seleziona tipo di evento",
        step2: "Inserisci i dettagli",
        presets: {
          fiera: { name: "Fiera Media" },
          expo: { name: "Expo" },
          festival: { name: "Festival" },
        },
        custom: {
          name: "Personalizzato",
          desc: "Inserisci i tuoi parametri",
          superficie: "Superficie (m²)",
          visitatori: "Visitatori/giorno",
        },
        visitorsDay: "visitatori/giorno",
        calculate: "Calcola Impatto",
        reset: "Nuovo Calcolo",
        results: {
          title: "L'Impatto del Tuo Evento",
          recyclingRate: "Raccolta Differenziata",
          recyclingRateDesc: "Tasso di riciclo raggiungibile con tecnologia Ganiga",
          wasteSaved: "Rifiuti Sottratti alla Discarica",
          wasteSavedDesc: "Kg di indifferenziata convertiti in materiale riciclabile al giorno",
          co2Saved: "CO₂ Risparmiata",
          co2SavedDesc: "Emissioni evitate grazie al corretto smistamento al giorno",
          waterSaved: "Acqua Risparmiata",
          waterSavedDesc: "Litri risparmiati nei processi produttivi al giorno",
          cta: "Vuoi trasformare questi numeri in realtà? Contattaci per un piano personalizzato.",
          contact: "Richiedi Preventivo",
        },
      },
    },
  },

  en: {
    // Header
    nav: {
      home: "Home",
      products: "Products",
      technology: "Technology",
      benefits: "Benefits",
      configurator: "Configurator",
      rental: "Rentals",
      contact: "Contact",
      rentForEvents: "Rent",
      requestQuote: "Buy",
      askInfo: "Ask Info",
      openMenu: "Open menu",
      closeMenu: "Close menu",
    },

    // Hero
    hero: {
      badge: "AI that makes a real impact on the planet",
      headline: "Turn",
      headlineHighlight1: "waste",
      headlineIn: "into",
      headlineHighlight2: "data.",
      subheadline: "Our proprietary AI automates sorting with",
      subheadlineStrong1: "95% purity",
      subheadlineReduces: ", turning every discard into",
      subheadlineStrong2: "audit‑ready KPIs",
      subheadlineGuarantees: "to make your",
      subheadlineStrong3: "ESG goals measurable",
      subheadlineImmediate: "in real time.",
      ctaPrimary: "Buy",
      ctaSecondary: "Rent",
    },

    // Partners
    partners: {
      title: "Clients improving their brand and impact with us",
      subtitle: "Because ignoring environmental impact today is not just a moral error, but also bad business.",
    },

    // Products
    products: {
      title: "Our suite of",
      titleHighlight: "Hoooly",
      subtitle: "AI that becomes Hardware for every need.",
      indoor: {
        name: "HooolyIndoor",
        tagline: "High-volume automatic sorter",
        idealFor: "Food courts, Malls, Airports",
        specs: ["Volume: 240 L", "Touch display", "AI < 1 s", "Plug & Play", "Real-time monitoring"],
      },
      small: {
        name: "HooolySmall",
        tagline: "Guided multi-compartment bin",
        idealFor: "Offices, Retail",
        specs: ["Volume: 180 L", "Touch monitor", "AI < 1 s", "Plug & Play", "Real-time monitoring"],
      },
      food: {
        name: "HooolyFood",
        tagline: "Food waste sensor",
        idealFor: "Canteens, Restaurants, Hotels",
        specs: ["Dish recognition", "Automatic weighing", "Recommended strategies", "Cost optimization"],
      },
      dashboard: {
        name: "HooolyDashboard",
        tagline: "Intelligence Platform",
        idealFor: "Management, Sustainability",
        specs: ["AI Agent", "ESG Reports", "Data & KPI export"],
      },
      techTitle: "The performance that",
      techTitleHighlight: "Hoooly",
      techTitleEnd: "guarantees",
      techSubtitle: "Not just ideals, but solid technology.",
      techFeatures: {
        aiRecognition: {
          title: "AI Recognition",
          stat: "95%",
          statLabel: "certified accuracy",
          description: "Real-time recognition in less than 1s of materials and brands with 95% certified accuracy.",
        },
        sorting: {
          title: "Enhanced sorting",
          stat: "-40%",
          statLabel: "mixed waste volume",
          description: "Only 5% foreign fractions in the various categories.",
        },
        continuity: {
          title: "Operational continuity",
          stat: "24/7",
          statLabel: "guaranteed service",
          description: "Service guaranteed 24/7, even in case of failure.",
        },
        design: {
          title: "Plug & play design",
          stat: "5min",
          statLabel: "installation",
          description: "Installation in 5 minutes, integrated Wi-Fi and 4G.",
        },
      },
    },

    // Benefits (Bento)
    benefits: {
      title: "Sustainability that becomes",
      titleHighlight: "ROI",
      subtitle: "Europe is imposing new rules. Those who don't comply pay more, lose clients, and lose access to credit.",
      features: {
        efficiency: {
          title: "Operational efficiency",
          stat: "-40%",
          statLabel: "operational management costs",
          description: "Intelligent monitoring, optimized emptying, and waste disposal cost reduction powered by Computer Vision AI.",
        },
        compliance: {
          title: "Cost of capital",
          stat: "+2%",
          statLabel: "bank interest for low ESG rating",
          description: "Banks assess climate risk. Companies that don't track waste are \"high risk\". Hoooly improves the Environmental rating, lowering borrowing costs and unlocking Green Loans.",
        },
        fiscal: {
          title: "Taxes & penalties",
          stat: "TARI",
          statLabel: "variable quota reduction",
          description:
            "Plastic Tax, rising disposal tariffs: poor waste management costs thousands per year. Hoooly reduces unsorted waste and lowers the tax base, backed by certified data.",
        },
        sentiment: {
          title: "Greenwashing risk",
          stat: "CSRD",
          statLabel: "mandatory certified reporting",
          description: "CSRD mandates auditable environmental impact data. Without real traceability, companies face legal penalties and reputational damage. Hoooly certifies every gram, material by material.",
        },
      },
      cta: "Want to see results in your context?",
      ctaLink: "Request a demo →",
    },

    // Configurator
    configurator: {
      title: "Simulate the impact of hoooly",
      titleHighlight: "in your spaces",
      subtitle: "In less than 1 minute, discover savings, efficiency, and real environmental impact.",
      subtitleInfo: "Every single kg of waste that our solutions divert from mixed waste for recovery saves the environment 20 liters of water and 1.5 kg of CO₂.",
      requestConsultation: "Request a free consultation",
      step1: "Select your sector",
      step2: "Enter your data",
      calculate: "Discover your solution",
      results: "Your results",
      resultsSubtitle: "Here's the possible impact",
      newCalculation: "New calculation",
      goToConfigurator: "Go to Configurator",
      sectors: {
        offices: {
          label: "Medium footfall",
          description: "Offices, Retail",
        },
        retail: {
          label: "High footfall",
          description: "Malls, Airports",
        },
        canteens: {
          label: "Canteens",
          description: "HoReCa, Hospitals, Corporate",
        },
      },
      inputs: {
        dailyFootfall: "People who use the space every day",
        dailyMeals: "Daily meals",
        placeholderFootfall: "e.g. 50",
        placeholderRetail: "e.g. 1000",
        placeholderMeals: "e.g. 200",
        surface: "Surface area (sqm)",
        placeholderSurface: "e.g. 500",
        emptyingsPerDay: "Daily emptyings",
        emptyingsQuestion: "How many times per day is cleaning/emptying performed?",
        percentIndiff: "% Mixed waste",
        percentIndiffDesc: "Percentage of mixed waste compared to total",
        percentIndiffPreset: "Pre-set to sector average, can be modified",
        kgWasteDay: "Kg waste/day",
        placeholderKgWaste: "e.g. 10",
        kgWasteDesc: "Total daily waste produced",
        kgWasteAutoCalc: "Auto-calculated based on footfall, can be modified",
      },
      results_labels: {
        wasteReduction: "Mixed Waste Reduction",
        foodWasteReduction: "Waste Reduction",
        wasteDescDynamic: "From {from}% to 10% mixed waste",
        wasteDescAlreadyOptimal: "Your impact is already amazing! Keep the 10% mixed waste",
        foodWasteDesc: "50% food waste reduction",
        co2Saved: "CO₂ Saved",
        co2Desc: "Coefficient: {coeff} kg CO₂ per kg waste",
        waterSaved: "Water Saved",
        waterDesc: "Coefficient: {coeff} L per kg waste",
        logistics: "Logistics Optimization",
        logisticsDesc: "Emptyings saved thanks to proper sorting",
        emptyingsPerDay: "emptyings/day",
        volumeInfo: "Bin volume: {volume}L • Density: {density} kg/L",
        purchaseAdvice: "Purchase Advice",
        purchaseDesc: "Recommended number of {product} units",
        units: "units",
        product: "Product",
        calculatedBased: "Calculated based on waste volume ({rt} kg/day) and surface area ({area} sqm)",
        methodology:
          "The savings parameters are calculated based on national LCA (Life Cycle Assessment) averages and FAO/ISPRA reports, referring to the environmental impact avoided in raw material production and disposal processes.",
        methodologyTooltip: "Parameters based on Life Cycle Assessment and FAO/ISPRA reports",
      },
    },

    // Services
    services: {
      title: "Activation",
      titleHighlight: "models",
      purchase: {
        title: "Purchase",
        subtitle: "For Offices, Malls, Airports, Retail",
        description: "Proprietary CAPEX investment.",
        features: ["Device ownership", "SaaS Dashboard included", "24/7 priority support"],
        cta: "Request Quote",
      },
      longRental: {
        title: "Long-term Rental",
        subtitle: "12-36 month contract",
        description: "OpEx model with monthly fee and maintenance included.",
        features: ["No initial investment", "Maintenance included", "Guaranteed technology upgrade"],
        cta: "Discover Plans",
      },
      shortRental: {
        title: "Short-term Rental",
        subtitle: "For Festivals, Trade Shows and Events",
        description: "Flexible rental with setup and pickup included.",
        features: ["Setup and pickup included", "Post-event report", "On-site assistance"],
        cta: "Rent Now",
      },
    },

    // Awards
    awards: {
      badge: "Awards",
      title: "Awards and Recognition",
      subtitle: "Our innovation has been recognized internationally",
      items: {
        lifeTriplAir: {
          title: "European Life Tripl-Air Tender",
          subtitle: "Selected for the European tender",
        },
        googleAI: {
          title: "Google AI Sustainability Award",
          subtitle: "AI Groundbreaker Award winners",
        },
        luiss: {
          title: "Non Sprecare Award",
          subtitle: "LUISS University Award winners - Rome",
        },
        techcrunch: {
          title: "TechCrunch Disrupt 2025",
          subtitle: "Selected for San Francisco",
        },
      },
    },

    // Contact
    contact: {
      title: "Let's talk about your",
      titleHighlight: "project",
      subtitle: "Fill out the form and we'll get back to you within 24 hours with a personalized proposal.",
      location: "Location",
      email: "Email",
    },

    // Footer
    footer: {
      description: "The first AI platform that turns waste into data for sustainable companies.",
      products: "Products",
      company: "Company",
      solutions: "Solutions",
      solutionLinks: {
        tariReduction: "TARI Tax Reduction",
        esgReporting: "CSRD & ESG Compliance",
        eventRental: "Event Rental",
        operationalCosts: "Plastic Tax & Green Loans",
      },
      aboutUs: "About Us",
      caseStudy: "Case Study",
      careers: "Careers",
      contact: "Contact",
      legal: "Legal",
      privacyPolicy: "Privacy Policy",
      cookiePolicy: "Cookie Policy",
      companyName: "Company Name",
      headquarters: "Headquarters",
      vatNumber: "VAT / Tax ID",
      copyright: "© {year} Ganiga Innovation Srl. All rights reserved.",
      madeWith: "Made with 💚 in Italy",
    },

    // Careers
    careers: {
      title: "Join",
      titleHighlight: "our team",
      subtitle: "We're looking for talent, vision, speed.",
      firstName: "First Name",
      lastName: "Last Name",
      email: "Email",
      role: "Role of interest",
      rolePlaceholder: "Select a role",
      cv: "Attach CV",
      cvUpload: "Click to upload your CV",
      cvFormat: "PDF or Word, max 10MB",
      message: "Anything else to add?",
      messageOptional: "(optional)",
      messagePlaceholder: "Tell us about yourself, your motivations, or what caught your attention about Ganiga...",
      submit: "Submit Application",
      submitting: "Submitting...",
      privacy: "Your data will be processed according to our Privacy Policy.",
      errors: {
        fileTooLarge: "File is too large. Maximum 10MB.",
        invalidFormat: "Unsupported format. Use PDF or Word.",
        fillRequired: "Please fill in all required fields.",
        attachCV: "Please attach your CV.",
      },
      success: "Application submitted successfully! We'll contact you soon.",
      roles: {
        other: "Other",
      },
    },

    // Rental Page
    rental: {
      badge: "Event Rental",
      heroTitle: "Integrated Event Waste Management",
      heroTitleHighlight: "ISO 20121",
      heroSubtitle:
        "End-to-end facility management: Hoooly AI technology, dedicated on-site staff and certified reporting for maximum ESG compliance.",
      heroSubtitleBold: ["facility management", "Hoooly AI technology", "dedicated on-site staff", "certified reporting", "ESG compliance"],
      heroIsoBadge: "ISO 20121 Ready",
      ctaPrimary: "Request a Facility Management Plan",
      ctaSecondary: "Discover Benefits",

      // Compliance Highlight Box (post-hero)
      complianceBox: {
        label: "ISO 20121 Standard",
        text: "Our digital reports and AI sorting accuracy (>95%) provide the technical evidence required to certify your event and support CSRD reporting.",
      },

      // Facility Management Pillars
      pillars: {
        title: "Smart Waste Operations: the service in 3 pillars",
        subtitle: "A single partner for hardware, on-site operations and certified data.",
        items: {
          hardware: {
            label: "Hardware",
            title: "Smart Bin Hoooly",
            description: "Rental of the Hoooly Indoor and Small fleet with AI recognition and native 4G connectivity.",
          },
          staff: {
            label: "Staff",
            title: "On-site Operations",
            description: "Ganiga personnel on-site for flow management, bin emptying and continuous support.",
          },
          data: {
            label: "Data",
            title: "Dashboard & Reports",
            description: "Real-time monitoring and a certified post-event report, ready for ESG and ISO 20121 audits.",
          },
        },
      },

      // Operational Excellence Section
      operationalTitle: "Operational Excellence",
      operationalSubtitle: "Three pillars for worry-free management",
      operational: {
        plugPlay: {
          title: "Plug & Play",
          description: "Installation in",
          descriptionEnd: ", native 4G connection.",
        },
        aiSorting: {
          title: "AI Sorting",
          descriptionPre: "Precision at",
          descriptionMid: ", reduces residual waste to",
          descriptionEnd: ".",
        },
      },

      // ISO 20121 Section
      iso: {
        badge: "ISO 20121 Advantage",
        title: "ISO 20121 & CSRD Compliance",
        subtitle: "Three pieces of technical evidence that make your event certifiable.",
        columns: [
          {
            title: "Audit-Ready Data",
            description: "Digital reports with full flow traceability (kg, CO₂, purity) for ISO 20121 and CSRD reporting.",
          },
          {
            title: "95% Sorting Accuracy",
            description: "Guaranteed AI precision: we exceed legal minimums on recycling targets.",
          },
          {
            title: "ESG Reputation",
            description: "Tangible value for sponsors and stakeholders: improve your event's sustainability rating.",
          },
        ],
      },

      // Data Impact Section
      dataImpact: {
        title: "The Numbers That Matter",
        subtitle: "Measurable KPIs that demonstrate mathematical efficiency, not just theoretical",
        precision: {
          title: "AI Precision",
          description: "Automatic material recognition",
        },
        waste: {
          title: "Residual Waste",
          description: "Reduced to the minimum",
        },
        costs: {
          title: "Logistics Costs",
          description: "Optimized emptyings and staff",
        },
      },

      comparison: {
        title: "Why choose Ganiga?",
        without: {
          title: "Without Ganiga",
          items: ["Contaminated recycling", "High disposal costs", "Uncertain environmental impact"],
        },
        with: {
          title: "With Ganiga",
          items: ["95% recognition accuracy", "Only 10% residual waste", "Ready-to-use ESG report"],
        },
      },

      // Product Fleet Section
      fleetTitle: "Your Smart Fleet",
      fleetSubtitle: "Two complementary solutions to cover every need of your event",
      indoor: {
        title: "Hoooly Indoor",
        subtitle: "High-volume automatic sorter",
        badge: "INDOOR",
        description: "Built-in 4G connection: no venue Wi-Fi needed, turn on and you're ready.",
        idealFor: "Ideal for: Food Areas, Lounge Zones, Main Entrances",
        specs: {
          volume: "Volume",
          volumeValue: "240 L",
          display: "Display",
          displayValue: "Touch",
          ai: "AI Recognition",
          aiValue: "< 1s",
          setup: "Connection",
          setupValue: "4G built-in",
        },
      },
      small: {
        title: "Hoooly Small",
        subtitle: "Guided multi-compartment bin",
        badge: "OUTDOOR",
        description: "Built-in 4G connection: no venue Wi-Fi needed, turn on and you're ready.",
        idealFor: "Ideal for: Outdoor Areas, Corridors, Secondary Zones",
        specs: {
          volume: "Volume",
          volumeValue: "180 L",
          display: "Monitor",
          displayValue: "Touch",
          ai: "AI Recognition",
          aiValue: "< 1s",
          setup: "Connection",
          setupValue: "4G built-in",
        },
      },

      // Revenue & Branding Section
      revenue: {
        title: "The Rental That Pays for Itself",
        description:
          "Turn a waste service into a marketing asset. Our Hoooly models are attention magnets: customize them with your sponsors' branding and create new high-visibility advertising spaces.",
        descriptionBold: ["marketing asset", "attention magnets", "high-visibility advertising spaces"],
        benefits: [
          "Brandable surface for sponsors",
          "Touch display for dynamic communication",
          "Final report with engagement metrics",
        ],
      },
      economicBenefit: {
        title: "Economic Benefit",
        description:
          "Less residual waste means drastically reduced waste disposal costs for the event organizer. Ganiga is not just a green choice, it's a budget optimization.",
        descriptionBold: ["drastically reduced waste disposal costs", "budget optimization"],
      },

      // ESG Reporting Section
      esg: {
        title: "All-Inclusive Facility Management Solution",
        subtitle:
          "Technology, operations and data: a single package for sustainable facility management of fairs, festivals and large events.",
      },
      includes: {
        setup: {
          title: "Zero Hassle",
          description: "Full logistics, strategic bin placement and end-of-event collection.",
        },
        support: {
          title: "Dedicated Staff",
          description: "Specialized Ganiga personnel on-site for flow monitoring and bin emptying.",
        },
        report: {
          title: "Data & Reports",
          description: "Live dashboard and final report with KPIs: CO₂, kg per material, sorting purity.",
        },
        marketing: {
          title: "Sponsor Branding",
          description: "Customize bins with your sponsors' branding for new advertising visibility.",
        },
      },

      // FAQ Section (AI/SEO indexing)
      faq: {
        title: "Frequently Asked Questions",
        subtitle: "Everything you need to know about sustainable event facility management",
        items: [
          {
            q: "How does Ganiga help achieve ISO 20121?",
            a: "Ganiga provides certified, traceable data on event waste management — a key requirement of the ISO 20121 standard. Hoooly stations record flows, weight per material and sorting purity in real time, generating audit-ready reports that eliminate manual estimates.",
          },
          {
            q: "Does Ganiga staff also handle bin emptying?",
            a: "Yes. Our facility management package includes dedicated on-site staff throughout the event: flow monitoring, bin emptying, operations and technical support. You focus on the event, we handle the full waste cycle.",
          },
          {
            q: "How is event waste data calculated?",
            a: "Each Hoooly station uses computer vision AI to recognize and classify materials with 95% precision. Data (kg per category, CO₂ avoided, purity, fill level) is collected in real time over 4G and aggregated in a live dashboard, with a detailed final report delivered at end of event.",
          },
          {
            q: "What kind of events is the service suited for?",
            a: "Fairs, festivals, expos, conferences, sports and cultural events. The service scales from a few hundred to tens of thousands of visitors per day, indoor and outdoor, thanks to the Hoooly Indoor and Small fleet and native 4G connectivity that requires no on-site infrastructure.",
          },
        ],
      },

      // Contact Form
      contactTitle: "Design your Sustainable Event from A to Z",
      contactSubtitle: "Fill out the form and receive a customized facility management plan within 24 hours.",
      form: {
        firstName: "First Name",
        firstNamePlaceholder: "Your first name",
        lastName: "Last Name",
        lastNamePlaceholder: "Your last name",
        email: "Email",
        emailPlaceholder: "example@email.com",
        phone: "Phone",
        phonePlaceholder: "Optional",
        phoneOptional: "(optional)",
        eventStartDate: "Event start date",
        eventDuration: "Event duration (in days)",
        eventDurationPlaceholder: "e.g. 3",
        eventType: "Event type",
        eventTypePlaceholder: "e.g. Festival, Trade Fair, Expo",
        expectedAttendance: "Expected attendance",
        expectedAttendancePlaceholder: "e.g. 5000",
        description: "Description",
        descriptionPlaceholder: "Briefly describe your event...",
        company: "Company/Organization",
        companyPlaceholder: "Company name",
        location: "Event location",
        locationPlaceholder: "e.g. Milan, Fiera Rho",
        locationType: "Location type",
        locationTypeOutdoor: "Outdoor",
        locationTypeIndoor: "Indoor",
        locationTypeBoth: "Both",
        collectionPoints: "Desired number of collection points",
        collectionPointsPlaceholder: "e.g. 10",
      },
      configurator: {
        title: "Calculate Your Event's Impact",
        subtitle: "Select a preset or enter your parameters to discover the potential environmental impact",
        step1: "Select event type",
        step2: "Enter details",
        presets: {
          fiera: { name: "Trade Fair" },
          expo: { name: "Expo" },
          festival: { name: "Festival" },
        },
        custom: {
          name: "Custom",
          desc: "Enter your parameters",
          superficie: "Area (sqm)",
          visitatori: "Visitors/day",
        },
        visitorsDay: "visitors/day",
        calculate: "Calculate Impact",
        reset: "New Calculation",
        results: {
          title: "Your Event's Impact",
          recyclingRate: "Recycling Rate",
          recyclingRateDesc: "Recycling rate achievable with Ganiga technology",
          wasteSaved: "Waste Diverted from Landfill",
          wasteSavedDesc: "Kg of mixed waste converted to recyclable material per day",
          co2Saved: "CO₂ Saved",
          co2SavedDesc: "Emissions avoided through correct sorting per day",
          waterSaved: "Water Saved",
          waterSavedDesc: "Liters saved in production processes per day",
          cta: "Want to turn these numbers into reality? Contact us for a customized plan.",
          contact: "Request Quote",
        },
      },
    },
  },
} as const;

export type Translations = typeof translations.it;
